import React, { useCallback, useMemo } from 'react';
import type { TerminalClause } from '@atlaskit/jql-ast';
import type { TriggerProps } from '@atlaskit/popup';
import { Box, Flex, xcss, Text } from '@atlaskit/primitives';
import { type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import getNewJqlFromDoubleClausePickerJqlData from '@atlassian/jira-jql-utils/src/utils/get-jql-from-jql-data.tsx';
import type { OnSearchWrapper } from '../../../../common/types.tsx';
import PickerTrigger from '../../../../common/ui/picker-trigger/main.tsx';
import { useAST } from '../../../../controllers/ast/index.tsx';
import type { AnalyticsProps } from '../../../../utils/send-option-analytics.tsx';
import { NumberRangePickerPopup } from './main.tsx';
import type { NumberRangeValue } from './types.tsx';
import {
	getJqlDataFromClauses,
	getTriggerValueMessageDescriptor,
	isNumberRangeValueEmpty,
	mapJqlDataToNumberRangeValue,
	mapNumberRangeValueToJqlData,
} from './utils.tsx';

type NumberRangePickerProps = {
	fieldId: string;
	fieldLabel?: string;
	activePopupKey: string;
	clause: TerminalClause | undefined;
	secondaryClause: TerminalClause | undefined;
	onSearchWrapper?: OnSearchWrapper;
	onSendOptionAnalytics?: (attributes: AnalyticsProps) => void;
	onClear: (onClearJqlClause: string | undefined) => void;
	onOpen: () => void;
	onClose: () => void;
	fieldDescription: string | null;
	isIrremovable?: boolean;
};

export const NumberRangePicker = ({
	fieldId,
	fieldLabel,
	activePopupKey,
	clause,
	secondaryClause,
	onSearchWrapper,
	onSendOptionAnalytics,
	onClear,
	onOpen,
	onClose,
	fieldDescription,
	isIrremovable,
}: NumberRangePickerProps) => {
	const { formatMessage } = useIntl();
	const [ast] = useAST();

	/**
	 * fieldLabel comes from field displayName from the hydration data.
	 * It is defined in the fragment `jqlEditor_jqlBuilderBasic_JQLEditorUI` in
	 * `src/packages/jql-builder/basic/src/ui/jql-editor/index.tsx`
	 * we never expect it to be undefined but since we are not using the @required
	 * directive in the fragment, this check is required.
	 */
	const safeFieldLabel = fieldLabel ?? '';

	const numberRangeValue: NumberRangeValue = useMemo(
		() => mapJqlDataToNumberRangeValue(getJqlDataFromClauses(clause, secondaryClause)),
		[clause, secondaryClause],
	);

	const triggerValueMessageDescriptor: MessageDescriptor | undefined = useMemo(
		() => getTriggerValueMessageDescriptor(numberRangeValue),
		[numberRangeValue],
	);

	const isOpen = activePopupKey === fieldId;

	const handleOnChange = useCallback(
		(newValue: NumberRangeValue) => {
			onSearchWrapper &&
				onSearchWrapper(
					getNewJqlFromDoubleClausePickerJqlData(
						fieldId,
						ast,
						mapNumberRangeValueToJqlData(newValue),
					),
				);
			onClose();
			onSendOptionAnalytics &&
				onSendOptionAnalytics({
					hasMinValue: newValue.min.length > 0,
					hasMaxValue: newValue.max.length > 0,
				});
		},
		[ast, fieldId, onClose, onSearchWrapper, onSendOptionAnalytics],
	);

	const onTriggerClick = useCallback(
		() => (isOpen ? onClose() : onOpen()),
		[isOpen, onClose, onOpen],
	);

	const onTriggerClear = useCallback(
		() =>
			onClear(clause ? getNewJqlFromDoubleClausePickerJqlData(fieldId, ast, undefined) : undefined),
		[ast, clause, fieldId, onClear],
	);

	const triggerRenderer = useCallback(
		(triggerProps: TriggerProps) => (
			<PickerTrigger
				{...triggerProps}
				isInvalid={false}
				isSelected={isOpen}
				isLoading={false}
				hasValues={!isNumberRangeValueEmpty(numberRangeValue)}
				onClear={onTriggerClear}
				onClick={onTriggerClick}
				testId="jql-builder-basic.ui.jql-editor.field.number-range-picker.picker-trigger"
				fieldDescription={fieldDescription}
				isIrremovable={isIrremovable}
			>
				{triggerValueMessageDescriptor ? (
					<Flex as="span" alignItems="center">
						<Text as="strong" color="inherit">
							{safeFieldLabel}:&nbsp;
						</Text>

						<Box xcss={valueContainerStyles} as="span">
							{formatMessage(triggerValueMessageDescriptor, numberRangeValue)}
						</Box>
					</Flex>
				) : (
					<Flex as="span">{safeFieldLabel}</Flex>
				)}
			</PickerTrigger>
		),
		[
			isOpen,
			numberRangeValue,
			onTriggerClear,
			onTriggerClick,
			fieldDescription,
			isIrremovable,
			triggerValueMessageDescriptor,
			safeFieldLabel,
			formatMessage,
		],
	);

	return (
		<NumberRangePickerPopup
			isOpen={isOpen}
			onClose={onClose}
			onChange={handleOnChange}
			value={numberRangeValue}
			renderTrigger={triggerRenderer}
		/>
	);
};

const valueContainerStyles = xcss({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	maxWidth: '200px',
});
